import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowDown,
  ArrowUp,
  GitBranch,
  Info,
  Layers,
  Loader2,
  Settings2,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateHouseLimits } from "../../redux/operatorConsoleSlice";
import { isBetHousePartnershipReadyForConsole } from "../../utils/betHousePartners";
import { formatCash } from "../../utils/operatorConsoleHelpers";

const HouseLimitsModal = ({ open, onClose, bet, operatorUserId, onSuccess }) => {
  const dispatch = useDispatch();
  const { houseSettingsSaving, houseSettingsError } = useSelector((s) => s.operatorConsole);

  const [minSelections, setMinSelections] = useState("");
  const [minBet, setMinBet] = useState("");
  const [maxBet, setMaxBet] = useState("");
  const [matchingEnabled, setMatchingEnabled] = useState(false);
  const [matchingMaxPerTicket, setMatchingMaxPerTicket] = useState("");
  const [matchingMaxTotalExposure, setMatchingMaxTotalExposure] = useState("");
  const [errors, setErrors] = useState({});

  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);

  useEffect(() => {
    if (open && bet) {
      setMinSelections(String(bet.minSelectionsPerBet ?? ""));
      setMinBet(String(bet.defaultMinBetAmount ?? ""));
      setMaxBet(String(bet.defaultMaxBetAmount ?? ""));
      setMatchingEnabled(Boolean(bet.matching?.enabled));
      setMatchingMaxPerTicket(String(bet.matching?.maxPerTicket ?? ""));
      setMatchingMaxTotalExposure(String(bet.matching?.maxTotalExposure ?? ""));
      setErrors({});
    }
  }, [open, bet]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !houseSettingsSaving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, houseSettingsSaving, onClose]);

  if (!open || !bet) return null;

  const validate = () => {
    const next = {};
    if (minSelections.trim()) {
      const n = Number(minSelections);
      if (!Number.isInteger(n) || n < 1) next.minSelections = "Whole number ≥ 1";
      else if (n > 50) next.minSelections = "Max 50 games";
    }
    if (minBet.trim()) {
      const n = Number(minBet);
      if (Number.isNaN(n) || n < 0) next.minBet = "Valid amount ≥ 0";
    }
    if (maxBet.trim()) {
      const n = Number(maxBet);
      if (Number.isNaN(n) || n < 0) next.maxBet = "Valid amount ≥ 0";
    }
    if (
      minBet.trim() &&
      maxBet.trim() &&
      !next.minBet &&
      !next.maxBet &&
      Number(maxBet) > 0 &&
      Number(maxBet) < Number(minBet)
    ) {
      next.maxBet = "Must be ≥ minimum bet";
    }
    if (matchingMaxPerTicket.trim()) {
      const n = Number(matchingMaxPerTicket);
      if (Number.isNaN(n) || n < 0) next.matchingMaxPerTicket = "Valid amount ≥ 0";
    }
    if (matchingMaxTotalExposure.trim()) {
      const n = Number(matchingMaxTotalExposure);
      if (Number.isNaN(n) || n < 0) next.matchingMaxTotalExposure = "Valid amount ≥ 0";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partnershipReady || !operatorUserId || !validate()) return;

    const payload = { betId: bet._id, operatorUserId };
    let changed = false;

    if (minSelections.trim() && Number(minSelections) !== bet.minSelectionsPerBet) {
      payload.minSelectionsPerBet = Number(minSelections);
      changed = true;
    }
    if (minBet.trim() && Number(minBet) !== bet.defaultMinBetAmount) {
      payload.defaultMinBetAmount = Number(minBet);
      changed = true;
    }
    if (maxBet.trim() && Number(maxBet) !== bet.defaultMaxBetAmount) {
      payload.defaultMaxBetAmount = Number(maxBet);
      changed = true;
    }
    if (matchingEnabled !== Boolean(bet.matching?.enabled)) {
      payload.matchingEnabled = matchingEnabled;
      changed = true;
    }
    if (
      matchingMaxPerTicket.trim() &&
      Number(matchingMaxPerTicket) !== Number(bet.matching?.maxPerTicket ?? 0)
    ) {
      payload.matchingMaxPerTicket = Number(matchingMaxPerTicket);
      changed = true;
    }
    if (
      matchingMaxTotalExposure.trim() &&
      Number(matchingMaxTotalExposure) !==
        Number(bet.matching?.maxTotalExposure ?? 0)
    ) {
      payload.matchingMaxTotalExposure = Number(matchingMaxTotalExposure);
      changed = true;
    }

    if (!changed) {
      setErrors({ form: "Change at least one field before saving." });
      return;
    }

    try {
      await dispatch(updateHouseLimits(payload)).unwrap();
      onSuccess?.();
      onClose();
    } catch {
      /* error in redux */
    }
  };

  const formError = errors.form || (houseSettingsError ? String(houseSettingsError) : null);

  return createPortal(
    <div className="oc-treasury-modal-root oc-limits-modal-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="oc-treasury-modal-backdrop"
        aria-label="Close"
        onClick={() => !houseSettingsSaving && onClose()}
      />
      <form
        className={`oc-settings-modal-sheet ${houseSettingsSaving ? "oc-treasury-modal-sheet-busy" : ""}`}
        onSubmit={handleSubmit}
      >
        {houseSettingsSaving ? (
          <div className="oc-treasury-modal-busy" aria-label="Updating">
            <Loader2 size={24} className="oc-treasury-modal-spin" />
          </div>
        ) : null}

        <header className="oc-settings-modal-head">
          <div className="oc-treasury-modal-head-main">
            <span className="oc-treasury-modal-icon">
              <Settings2 size={18} />
            </span>
            <div className="oc-settings-modal-head-copy">
              <p className="oc-treasury-modal-eyebrow">Betting rules</p>
              <h2 id="oc-limits-modal-title">House limits</h2>
              <p className="oc-treasury-modal-sub">
                Minimum legs and stake bounds for new tickets.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="oc-treasury-modal-close"
            onClick={onClose}
            disabled={houseSettingsSaving}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="oc-settings-modal-body">
          {!partnershipReady ? (
            <div className="oc-settings-alert" role="status">
              All partners must accept before you can change house limits.
            </div>
          ) : null}

          <div className="oc-settings-current-box">
            <div className="oc-settings-current-head">
              <Info size={14} aria-hidden />
              <span>Current configuration</span>
            </div>
            <div className="oc-settings-current-grid">
              <div className="oc-settings-current-cell">
                <em>Min legs</em>
                <strong className="tabular-nums">{bet.minSelectionsPerBet ?? "—"}</strong>
                <span>games</span>
              </div>
              <div className="oc-settings-current-cell">
                <em>Min bet</em>
                <strong className="tabular-nums">
                  {formatCash(bet.defaultMinBetAmount ?? 0, { compact: true })}
                </strong>
                <span>Cash</span>
              </div>
              <div className="oc-settings-current-cell">
                <em>Max bet</em>
                <strong className="tabular-nums">
                  {formatCash(bet.defaultMaxBetAmount ?? 0, { compact: true })}
                </strong>
                <span>Cash</span>
              </div>
            </div>
          </div>

          <div className="oc-settings-section-label">New values</div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <Layers size={13} aria-hidden />
              <span>Min selections</span>
            </div>
            <div
              className={`oc-settings-input-wrap ${
                errors.minSelections ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <input
                type="number"
                min={1}
                max={50}
                step={1}
                value={minSelections}
                onChange={(e) => setMinSelections(e.target.value)}
                disabled={!partnershipReady || houseSettingsSaving}
                placeholder={String(bet.minSelectionsPerBet ?? "2")}
                aria-invalid={Boolean(errors.minSelections)}
                aria-describedby={errors.minSelections ? "limits-min-sel-error" : "limits-min-sel-hint"}
              />
              <span className="oc-settings-input-suffix">games</span>
            </div>
            {errors.minSelections ? (
              <p id="limits-min-sel-error" className="oc-settings-field-error">
                {errors.minSelections}
              </p>
            ) : (
              <p id="limits-min-sel-hint" className="oc-settings-field-hint">
                Minimum fixtures per ticket (1–50).
              </p>
            )}
          </div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <ArrowDown size={13} aria-hidden />
              <span>Minimum bet</span>
            </div>
            <div
              className={`oc-settings-input-wrap ${
                errors.minBet ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <input
                type="number"
                min={0}
                step="any"
                value={minBet}
                onChange={(e) => setMinBet(e.target.value)}
                disabled={!partnershipReady || houseSettingsSaving}
                placeholder={String(bet.defaultMinBetAmount ?? "0")}
                aria-invalid={Boolean(errors.minBet)}
                aria-describedby={errors.minBet ? "limits-min-bet-error" : "limits-min-bet-hint"}
              />
              <span className="oc-settings-input-suffix">Cash</span>
            </div>
            {errors.minBet ? (
              <p id="limits-min-bet-error" className="oc-settings-field-error">
                {errors.minBet}
              </p>
            ) : (
              <p id="limits-min-bet-hint" className="oc-settings-field-hint">
                Lowest stake a bettor can place on a ticket.
              </p>
            )}
          </div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <ArrowUp size={13} aria-hidden />
              <span>Maximum bet</span>
            </div>
            <div
              className={`oc-settings-input-wrap ${
                errors.maxBet ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <input
                type="number"
                min={0}
                step="any"
                value={maxBet}
                onChange={(e) => setMaxBet(e.target.value)}
                disabled={!partnershipReady || houseSettingsSaving}
                placeholder={String(bet.defaultMaxBetAmount ?? "0")}
                aria-invalid={Boolean(errors.maxBet)}
                aria-describedby={errors.maxBet ? "limits-max-bet-error" : "limits-max-bet-hint"}
              />
              <span className="oc-settings-input-suffix">Cash</span>
            </div>
            {errors.maxBet ? (
              <p id="limits-max-bet-error" className="oc-settings-field-error">
                {errors.maxBet}
              </p>
            ) : (
              <p id="limits-max-bet-hint" className="oc-settings-field-hint">
                Use 0 for no maximum cap. Must be ≥ minimum bet.
              </p>
            )}
          </div>

          <div className="oc-settings-section-label">House matching</div>

          <div className="oc-settings-field">
            <label className="oc-settings-field-label" htmlFor="limits-matching-toggle">
              <GitBranch size={13} aria-hidden />
              <span>Match with other houses</span>
              <input
                id="limits-matching-toggle"
                type="checkbox"
                checked={matchingEnabled}
                onChange={(e) => setMatchingEnabled(e.target.checked)}
                disabled={!partnershipReady || houseSettingsSaving}
                style={{ marginLeft: "auto" }}
              />
            </label>
            <p className="oc-settings-field-hint">
              When on, your available balance can co-fund big tickets on partner
              houses (you earn the same share of profit as you contribute), and
              partner houses can help fund tickets yours cannot cover alone.
              Turning it off only stops new matches.
            </p>
          </div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <ArrowUp size={13} aria-hidden />
              <span>Max per matched ticket</span>
            </div>
            <div
              className={`oc-settings-input-wrap ${
                errors.matchingMaxPerTicket ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <input
                type="number"
                min={0}
                step="any"
                value={matchingMaxPerTicket}
                onChange={(e) => setMatchingMaxPerTicket(e.target.value)}
                disabled={!partnershipReady || houseSettingsSaving || !matchingEnabled}
                placeholder={String(bet.matching?.maxPerTicket ?? "0")}
                aria-invalid={Boolean(errors.matchingMaxPerTicket)}
              />
              <span className="oc-settings-input-suffix">Cash</span>
            </div>
            {errors.matchingMaxPerTicket ? (
              <p className="oc-settings-field-error">{errors.matchingMaxPerTicket}</p>
            ) : (
              <p className="oc-settings-field-hint">
                Most you contribute to a single ticket on another house. 0 = no cap.
              </p>
            )}
          </div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <Layers size={13} aria-hidden />
              <span>Max total matched exposure</span>
            </div>
            <div
              className={`oc-settings-input-wrap ${
                errors.matchingMaxTotalExposure ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <input
                type="number"
                min={0}
                step="any"
                value={matchingMaxTotalExposure}
                onChange={(e) => setMatchingMaxTotalExposure(e.target.value)}
                disabled={!partnershipReady || houseSettingsSaving || !matchingEnabled}
                placeholder={String(bet.matching?.maxTotalExposure ?? "0")}
                aria-invalid={Boolean(errors.matchingMaxTotalExposure)}
              />
              <span className="oc-settings-input-suffix">Cash</span>
            </div>
            {errors.matchingMaxTotalExposure ? (
              <p className="oc-settings-field-error">{errors.matchingMaxTotalExposure}</p>
            ) : (
              <p className="oc-settings-field-hint">
                Ceiling on your balance locked across all open matched tickets. 0 = no cap.
              </p>
            )}
          </div>

          <div className="oc-settings-tip">
            <Info size={14} aria-hidden />
            <p>Changes apply to new tickets only. Existing open tickets keep their original limits.</p>
          </div>

          {formError ? <p className="oc-settings-form-error">{formError}</p> : null}
        </div>

        <footer className="oc-settings-modal-foot">
          <button
            type="button"
            className="oc-treasury-cancel"
            onClick={onClose}
            disabled={houseSettingsSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="oc-treasury-submit"
            disabled={!partnershipReady || houseSettingsSaving}
          >
            Save limits
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default HouseLimitsModal;
